import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';

/**
 * Policy-enforcing wrapper around PrimeNG's MessageService. Toasts are
 * the right primitive ONLY for short, positive confirmations and for
 * actions the user can undo. For anything the user needs to act on —
 * a save that failed, a load that failed, a network problem — use
 * the <lib-inline-error>, <lib-empty-state> or <lib-page-banner>
 * components instead.
 *
 * Deliberately no `error()` method. Any code that used to call
 * `messages.add({ severity: 'error', … })` must choose the right
 * contextual primitive rather than reach for a toast.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly messages = inject(MessageService);

  /** Short positive confirmation. Auto-dismisses. */
  success(summary: string, detail?: string): void {
    this.messages.add({
      severity: 'success',
      summary,
      detail,
      life: 3000,
    });
  }

  /** Informational toast — e.g. "Your changes were saved to drafts". */
  info(summary: string, detail?: string): void {
    this.messages.add({
      severity: 'info',
      summary,
      detail,
      life: 3000,
    });
  }

  /**
   * Destructive-action confirmation that carries an undo. The toast
   * stays visible long enough for the user to click; `onUndo` fires
   * when they do. The caller is responsible for reversing the action.
   */
  undo(summary: string, onUndo: () => void, detail?: string): void {
    this.messages.add({
      severity: 'success',
      summary,
      detail,
      life: 8000,
      sticky: false,
      data: { onUndo },
      // The toast template in the host app renders an Undo button when
      // msg.data?.onUndo is a function; see app-level <p-toast> config.
    });
  }
}
